import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBagIcon, StarIcon, HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { createClient } from '@supabase/supabase-js';
import type { Product } from 'lib/burokshop/types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Kiosk {
  id: string;
  business_name: string;
  logo_url: string | null;
  description?: string | null;
  product_count?: number;
}

export default function AllKiosksPage() {
  const [kiosks, setKiosks] = useState<Kiosk[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadKiosks() {
      try {
        // Récupérer tous les kiosks actifs
        const { data: kiosksData, error: kiosksError } = await supabase
          .from('kiosk_profiles')
          .select('id, business_name, logo_url, description')
          .eq('is_active', true)
          .eq('is_live', true)
          .order('business_name', { ascending: true });

        if (kiosksError) throw kiosksError;

        if (kiosksData) {
          // Vérifier quels kiosks ont des produits actifs et compter leurs produits
          const kiosksWithProducts: Kiosk[] = [];
          
          for (const kiosk of kiosksData) {
            const { data: productsData, count } = await supabase
              .from('kiosk_products')
              .select('id', { count: 'exact' })
              .eq('kiosk_id', kiosk.id)
              .eq('is_active', true);
            
            if (productsData && productsData.length > 0) {
              kiosksWithProducts.push({
                id: kiosk.id,
                business_name: kiosk.business_name,
                logo_url: kiosk.logo_url,
                description: kiosk.description,
                product_count: count || productsData.length
              });
            }
          }
          
          setKiosks(kiosksWithProducts);
        }
      } catch (error) {
        console.error('Error loading kiosks:', error);
        setKiosks([]);
      } finally {
        setLoading(false);
      }
    }
    loadKiosks();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 py-12 px-4">
        <div className="mx-auto max-w-7xl">
          <div className="mb-8 h-12 w-64 animate-pulse rounded bg-neutral-200 dark:bg-neutral-700" />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-96 animate-pulse rounded-2xl bg-neutral-200 dark:bg-neutral-700" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!kiosks.length) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900 flex items-center justify-center px-4">
        <div className="text-center">
          <ShoppingBagIcon className="mx-auto h-16 w-16 text-neutral-400 dark:text-neutral-600 mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
            Aucun kiosk disponible
          </h2>
          <p className="text-neutral-600 dark:text-neutral-400">
            Aucune boutique disponible pour le moment
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100 dark:from-neutral-900 dark:via-neutral-800 dark:to-neutral-900">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#ff0442] via-[#ff1a4d] to-[#ff0442] pt-20 pb-16 px-4">
        <div className="mx-auto max-w-7xl text-center">
          <h1 className="mb-4 text-4xl font-bold text-white md:text-5xl lg:text-6xl">
            Découvrez nos Kiosks
          </h1>
          <p className="mx-auto max-w-2xl text-lg text-white/90">
            Explorez une sélection de boutiques uniques et leurs produits exclusifs
          </p>
        </div>
      </div>

      {/* Kiosks Grid */}
      <div className="mx-auto max-w-7xl px-4 py-12">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-neutral-900 dark:text-white">
              Tous les Kiosks ({kiosks.length})
            </h2>
            <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
              Parcourez nos boutiques partenaires
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {kiosks.map((kiosk) => (
            <KioskCard key={kiosk.id} kiosk={kiosk} />
          ))}
        </div>
      </div>
    </div>
  );
}

function KioskCard({ kiosk }: { kiosk: Kiosk }) {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isFollowingAnimating, setIsFollowingAnimating] = useState(false);

  useEffect(() => {
    async function loadProducts() {
      try {
        const { data, error } = await supabase
          .from('kiosk_products')
          .select('*')
          .eq('kiosk_id', kiosk.id)
          .eq('is_active', true)
          .order('updated_at', { ascending: false })
          .limit(4);

        if (error) throw error;

        if (data) {
          const mappedProducts: Product[] = data.map((row: any) => {
            const images = Array.isArray(row.images) ? row.images : [];
            const firstImage = images[0];
            const featuredImage = firstImage ? {
              url: typeof firstImage === 'string' ? firstImage : firstImage.url || firstImage,
              altText: row.name,
              width: 800,
              height: 800
            } : {
              url: '',
              altText: row.name,
              width: 800,
              height: 800
            };

            return {
              id: row.id,
              handle: row.name.toLowerCase().replace(/\s+/g, '-') + '-' + row.id,
              availableForSale: (row.stock_quantity ?? 0) > 0,
              title: row.name,
              description: row.description || '',
              descriptionHtml: row.description || '',
              options: [],
              priceRange: {
                maxVariantPrice: {
                  amount: (row.original_price || row.price).toString(),
                  currencyCode: 'XOF'
                },
                minVariantPrice: {
                  amount: row.price.toString(),
                  currencyCode: 'XOF'
                }
              },
              variants: [{
                id: row.id,
                title: 'Default',
                availableForSale: (row.stock_quantity ?? 0) > 0,
                selectedOptions: [],
                price: {
                  amount: row.price.toString(),
                  currencyCode: 'XOF'
                }
              }],
              featuredImage,
              images: images.map((img: any, index: number) => ({
                url: typeof img === 'string' ? img : img.url || img,
                altText: `${row.name} - ${index + 1}`,
                width: 800,
                height: 800
              })),
              seo: {
                title: row.name,
                description: row.description || row.name
              },
              tags: row.tags || [],
              updatedAt: row.updated_at || row.created_at
            };
          });

          setProducts(mappedProducts);
        }
      } catch (error) {
        console.error('Error loading kiosk products:', error);
        setProducts([]);
      } finally {
        setLoading(false);
      }
    }
    loadProducts();
  }, [kiosk.id]);

  return (
    <div className="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm transition-all duration-300 hover:shadow-xl dark:border-neutral-700 dark:bg-neutral-800">
      {/* Header with logo and info */}
      <div className="p-6">
        <Link 
          to={`/kiosk/${kiosk.id}`}
          className="mb-4 flex items-start gap-4 group/header"
        >
          {kiosk.logo_url ? (
            <div className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-xl ring-2 ring-neutral-200 dark:ring-neutral-700 transition-all duration-200 group-hover/header:ring-blue-400 group-hover/header:scale-105">
              <img
                src={kiosk.logo_url}
                alt={kiosk.business_name}
                className="h-full w-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          ) : (
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#ff0442] to-[#ff1a4d] transition-all duration-200 group-hover/header:scale-105">
              <ShoppingBagIcon className="h-8 w-8 text-white" />
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-bold text-neutral-900 dark:text-white group-hover/header:text-blue-600 dark:group-hover/header:text-blue-400 transition-colors">
              {kiosk.business_name}
            </h3>
            <div className="mt-1 flex items-center gap-2">
              <div className="flex items-center">
                <StarIcon className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="ml-1 text-sm font-semibold text-neutral-700 dark:text-neutral-300">4.8</span>
              </div>
              <span className="text-xs text-neutral-500 dark:text-neutral-400">(128 avis)</span>
            </div>
          </div>
        </Link>

        {kiosk.description && (
          <p className="mb-4 line-clamp-2 text-sm text-neutral-600 dark:text-neutral-400">
            {kiosk.description}
          </p>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            {kiosk.product_count || products.length} {kiosk.product_count === 1 ? 'produit' : 'produits'}
          </span>
        </div>
      </div>

      {/* Products preview */}
      {!loading && products.length > 0 && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 p-4">
          <div className="grid grid-cols-2 gap-3">
            {products.slice(0, 4).map((product) => (
              <Link
                key={product.id}
                to={`/product/${product.handle}`}
                className="group/product relative aspect-square overflow-hidden rounded-lg bg-neutral-100 dark:bg-neutral-900"
              >
                {product.featuredImage?.url ? (
                  <img
                    src={product.featuredImage.url}
                    alt={product.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover/product:scale-110"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ShoppingBagIcon className="h-8 w-8 text-neutral-400" />
                  </div>
                )}
                {/* Price overlay */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                  <span className="text-xs font-semibold text-white">
                    {parseInt(product.priceRange.minVariantPrice.amount).toLocaleString()} XOF
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Loading products skeleton */}
      {loading && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 p-4">
          <div className="grid grid-cols-2 gap-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="aspect-square animate-pulse rounded-lg bg-neutral-200 dark:bg-neutral-700" />
            ))}
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="border-t border-neutral-200 p-4 dark:border-neutral-700">
        <div className="flex gap-3">
          <button
            onClick={() => {
              setIsFollowingAnimating(true);
              setTimeout(() => {
                setIsFollowing(!isFollowing);
                setIsFollowingAnimating(false);
              }, 300);
            }}
            className={`relative flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-medium transition-all duration-300 ${
              isFollowing
                ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-lg shadow-pink-500/30'
                : 'bg-white border-2 border-neutral-300 text-neutral-700 hover:border-pink-400 hover:text-pink-600 dark:bg-neutral-800 dark:border-neutral-600 dark:text-neutral-300 dark:hover:border-pink-500 dark:hover:text-pink-400'
            } ${isFollowingAnimating ? 'scale-95' : 'scale-100 hover:scale-105'}`}
          >
            {isFollowing ? (
              <>
                <HeartIconSolid className="h-5 w-5 animate-pulse" />
                <span>Suivi</span>
              </>
            ) : (
              <>
                <HeartIcon className="h-5 w-5" />
                <span>Suivre</span>
              </>
            )}
            {isFollowingAnimating && (
              <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-pink-400 to-rose-400 animate-ping opacity-75" />
            )}
          </button>
          <Link
            to={`/kiosk/${kiosk.id}`}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#ff0442] to-[#ff1a4d] px-4 py-3 text-sm font-semibold text-white transition-all duration-200 hover:shadow-lg hover:shadow-[#ff0442]/30 hover:scale-105"
          >
            <ShoppingBagIcon className="h-5 w-5" />
            Visiter
          </Link>
        </div>
      </div>
    </div>
  );
}

